import type { AdminCardStatus } from '@carcheck/shared';
import Chip, { type ChipProps } from '@mui/material/Chip';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Card status badge chip for the cards queue + inspector-detail Cards tab
 * (AUT-126). Colours mirror the mockup's `badge--{success,warning,neutral}`
 * classes via MUI Chip `color`. Labels resolve through i18n
 * (`cards.status.<value>`; CLAUDE.md rule #11).
 *
 * Typed as a TOTAL `Record` over ALL 7 `AdminCardStatus` values (the FULL
 * `InspectorCardStatus` surface — admin reconciliation sees every state,
 * unlike the own-inspector list which hides `abandoned`). A total record
 * means an enum addition is a compile error here, not a runtime crash on an
 * unmapped value.
 *
 * Mapping (mockup-aligned):
 *   - active    → success  "привязана"  (the bound, usable state)
 *   - suspended → warning  "приостановлена"
 *   - expired   → warning  "истекла"
 *   - unbound   → default  "отвязана"
 *   - abandoned → default  "не завершена"
 *   - binding   → info     "привязка…"  (awaiting WebPay notifier)
 *   - failed    → error    "ошибка"
 */
type ChipColor = ChipProps['color'];

const STATUS_COLOR: Record<AdminCardStatus, ChipColor> = {
  active: 'success',
  suspended: 'warning',
  expired: 'warning',
  unbound: 'default',
  abandoned: 'default',
  binding: 'info',
  failed: 'error',
};

export const CardStatusBadge = ({ status }: { status: AdminCardStatus }): ReactElement => {
  const { t } = useTranslation();
  return <Chip size="small" color={STATUS_COLOR[status]} label={t(`cards.status.${status}`)} />;
};
